import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiUrl } from '../config/api';
import { ACCESO, mensajeError, armarArbol, hojasDe, coincide } from './dpermisosUtil';
import PermisosUsuario from './DPermisosUsuario';
import './GestionProducto.css';
import './DPermisos.css';

function NodoMenu({ nodo, nivel, marcados, onToggle, filtro, cerrados, onPlegar }) {
    if (!coincide(nodo, filtro)) return null;
    const hojas = hojasDe(nodo);
    const n = hojas.filter(id => marcados.has(id)).length;
    const esPadre = nodo.hijos.length > 0;
    const abierto = filtro ? true : !cerrados.has(nodo.id);
    return (
        <div>
            <div className={`dperm-nodo ${esPadre ? 'dperm-nodo-padre' : ''}`} style={{ paddingLeft: 8 + nivel * 22 }}>
                {esPadre
                    ? <button type="button" className="dperm-plegar" onClick={() => onPlegar(nodo.id)}
                              title={abierto ? 'Contraer' : 'Expandir'}>{abierto ? '▾' : '▸'}</button>
                    : <span className="dperm-plegar" />}
                <label>
                    <input type="checkbox" checked={n > 0 && n === hojas.length}
                           ref={el => { if (el) el.indeterminate = n > 0 && n < hojas.length; }}
                           onChange={(e) => onToggle(hojas, e.target.checked)} />
                    <span>{nodo.descripcion}</span>
                    {esPadre && <span className="dperm-contador">{n}/{hojas.length}</span>}
                </label>
            </div>
            {esPadre && abierto && nodo.hijos.map(h => (
                <NodoMenu key={h.id} nodo={h} nivel={nivel + 1} marcados={marcados} onToggle={onToggle}
                          filtro={filtro} cerrados={cerrados} onPlegar={onPlegar} />
            ))}
        </div>
    );
}

function DPermisos({ usuario }) {
    const [vista, setVista] = useState('grupos');            // grupos | usuarios
    const [grupos, setGrupos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [menus, setMenus] = useState([]);
    const [catalogo, setCatalogo] = useState([]);

    const [grupoSel, setGrupoSel] = useState(null);          // idGrupo seleccionado
    const [subTab, setSubTab] = useState('accesos');         // accesos | especiales | usuarios
    const [permisosGrupo, setPermisosGrupo] = useState([]);

    // Accesos al menu del grupo seleccionado (ids de opciones finales)
    const [marcados, setMarcados] = useState(new Set());
    const [marcadosGuardados, setMarcadosGuardados] = useState(new Set());
    const [filtroMenu, setFiltroMenu] = useState('');
    const [cerrados, setCerrados] = useState(new Set());
    const [guardando, setGuardando] = useState(false);

    const [modalGrupo, setModalGrupo] = useState(null);      // { edicion, idGrupo, nomGrupo }
    const [nuevoEspecial, setNuevoEspecial] = useState({ idMenu: '', permiso: '' });
    const [usuarioAgregar, setUsuarioAgregar] = useState('');
    const [busquedaUsuario, setBusquedaUsuario] = useState('');
    const [usuarioSel, setUsuarioSel] = useState(null);      // nombre del usuario seleccionado
    const [usuarioPendiente, setUsuarioPendiente] = useState(false);   // excepciones sin guardar
    const [modalUsuario, setModalUsuario] = useState(null);   // { usuario, email, clave, clave2, grupo }

    const arbol = useMemo(() => armarArbol(menus), [menus]);
    const idsHojas = useMemo(() => new Set(arbol.flatMap(hojasDe)), [arbol]);
    const grupo = grupos.find(g => g.idGrupo === grupoSel) || null;
    const hayCambios = marcados.size !== marcadosGuardados.size || [...marcados].some(id => !marcadosGuardados.has(id));

    // ===== Carga =====

    const cargarGrupos = useCallback(async () => {
        try { setGrupos((await axios.get(getApiUrl('/api/grupos-usuario'))).data); }
        catch (e) { toast.error(mensajeError(e, 'Error al cargar los grupos')); }
    }, []);

    const cargarUsuarios = useCallback(async () => {
        try { setUsuarios((await axios.get(getApiUrl('/api/usuarios'))).data); }
        catch (e) { toast.error(mensajeError(e, 'Error al cargar los usuarios')); }
    }, []);

    const cargarPermisosGrupo = useCallback(async (idGrupo) => {
        try {
            const { data } = await axios.get(getApiUrl(`/api/grupos-usuario/${encodeURIComponent(idGrupo)}/permisos`));
            setPermisosGrupo(data);
            const accesos = new Set(data.filter(p => p.permiso === ACCESO && p.estado === 'A').map(p => p.idMenu));
            setMarcados(accesos);
            setMarcadosGuardados(new Set(accesos));
        } catch (e) { toast.error(mensajeError(e, 'Error al cargar los permisos del grupo')); }
    }, []);

    useEffect(() => {
        cargarGrupos();
        cargarUsuarios();
        axios.get(getApiUrl('/api/permisos/menus')).then(r => setMenus(r.data))
            .catch(e => toast.error(mensajeError(e, 'Error al cargar las opciones de menu')));
        axios.get(getApiUrl('/api/permisos/catalogo')).then(r => setCatalogo(r.data)).catch(() => {});
    }, [cargarGrupos, cargarUsuarios]);

    useEffect(() => {
        if (grupoSel) cargarPermisosGrupo(grupoSel);
        else { setPermisosGrupo([]); setMarcados(new Set()); setMarcadosGuardados(new Set()); }
    }, [grupoSel, cargarPermisosGrupo]);

    const descartarPendientes = () =>
        !usuarioPendiente || window.confirm('Hay excepciones de acceso sin guardar. ¿Descartarlas?');

    const seleccionarUsuario = (nombre) => {
        if (nombre === usuarioSel || !descartarPendientes()) return;
        setUsuarioPendiente(false);
        setUsuarioSel(nombre);
    };

    const cambiarVista = (v) => {
        if (v === vista) return;
        if (vista === 'usuarios' && !descartarPendientes()) return;
        if (vista === 'grupos' && hayCambios && !window.confirm('Hay cambios de accesos sin guardar. ¿Descartarlos?')) return;
        setUsuarioPendiente(false);
        if (vista === 'grupos' && hayCambios) setMarcados(new Set(marcadosGuardados));
        setVista(v);
    };

    const seleccionarGrupo = (idGrupo) => {
        if (idGrupo === grupoSel) return;
        if (hayCambios && !window.confirm('Hay cambios de accesos sin guardar. ¿Descartarlos?')) return;
        setGrupoSel(idGrupo);
        setFiltroMenu('');
    };

    // ===== Grupos =====

    const guardarGrupo = async (e) => {
        e.preventDefault();
        const { edicion, idGrupo, nomGrupo } = modalGrupo;
        if (!idGrupo.trim()) { toast.warning('El codigo del grupo es obligatorio'); return; }
        if (!nomGrupo.trim()) { toast.warning('El nombre del grupo es obligatorio'); return; }
        try {
            if (edicion) {
                await axios.put(getApiUrl(`/api/grupos-usuario/${encodeURIComponent(idGrupo)}`), { nomGrupo });
                toast.success('Grupo actualizado');
            } else {
                await axios.post(getApiUrl('/api/grupos-usuario'), { idGrupo: idGrupo.trim(), nomGrupo });
                toast.success('Grupo creado');
                setGrupoSel(idGrupo.trim());
            }
            setModalGrupo(null);
            cargarGrupos();
            cargarUsuarios();
        } catch (err) { toast.error(mensajeError(err, 'Error al guardar el grupo')); }
    };

    const eliminarGrupo = async () => {
        if (!window.confirm(`¿Eliminar el grupo ${grupo.idGrupo} y todos sus permisos?`)) return;
        try {
            await axios.delete(getApiUrl(`/api/grupos-usuario/${encodeURIComponent(grupo.idGrupo)}`));
            toast.success('Grupo eliminado');
            setGrupoSel(null);
            cargarGrupos();
        } catch (e) { toast.error(mensajeError(e, 'Error al eliminar el grupo')); }
    };

    // ===== Accesos al menu =====

    const alternarAccesos = (ids, marcar) => setMarcados(prev => {
        const s = new Set(prev);
        ids.forEach(id => marcar ? s.add(id) : s.delete(id));
        return s;
    });

    const plegar = (id) => setCerrados(prev => {
        const s = new Set(prev);
        if (s.has(id)) s.delete(id); else s.add(id);
        return s;
    });

    const guardarAccesos = async () => {
        // Evitar que el operador se quite a si mismo el acceso a esta pantalla
        const miGrupo = usuarios.find(u => u.usuario === usuario)?.grupo;
        const estaPantalla = menus.find(m => m.url === 'dpermisos');
        if (miGrupo === grupoSel && estaPantalla && marcados.size > 0 && !marcados.has(estaPantalla.id)
            && !window.confirm('Usted pertenece a este grupo y esta quitando el acceso a "Grupos y Permisos". '
                + 'No podra volver a esta pantalla desde el menu. ¿Guardar de todos modos?')) return;
        setGuardando(true);
        try {
            await axios.put(getApiUrl(`/api/grupos-usuario/${encodeURIComponent(grupoSel)}/accesos`),
                { idMenus: [...marcados].filter(id => idsHojas.has(id)), idOperador: usuario || '' });
            toast.success('Accesos guardados');
            await cargarPermisosGrupo(grupoSel);
            cargarGrupos();
        } catch (e) { toast.error(mensajeError(e, 'Error al guardar los accesos')); }
        finally { setGuardando(false); }
    };

    // ===== Permisos especiales (todo lo que no es ACCESO) =====

    const especiales = permisosGrupo.filter(p => p.permiso !== ACCESO);
    const opcionesConPantalla = useMemo(
        () => menus.filter(m => m.url).sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || '')),
        [menus]);

    const agregarEspecial = async (e) => {
        e.preventDefault();
        const permiso = nuevoEspecial.permiso.trim();
        if (!nuevoEspecial.idMenu) { toast.warning('Seleccione la opcion de menu'); return; }
        if (!permiso) { toast.warning('Indique el permiso'); return; }
        if (permiso === ACCESO) { toast.warning('El acceso se define en la pestaña "Accesos al menu"'); return; }
        try {
            await axios.post(getApiUrl('/api/permisos'),
                { grupo: grupoSel, idMenu: Number(nuevoEspecial.idMenu), permiso, idOperador: usuario || '' });
            toast.success('Permiso asignado');
            setNuevoEspecial({ idMenu: '', permiso: '' });
            cargarPermisosGrupo(grupoSel);
            cargarGrupos();
        } catch (err) { toast.error(mensajeError(err, 'Error al asignar el permiso')); }
    };

    const cambiarEstadoPermiso = async (p) => {
        try {
            await axios.put(getApiUrl(`/api/permisos/${p.id}/estado`),
                { estado: p.estado === 'A' ? 'I' : 'A', idOperador: usuario || '' });
            cargarPermisosGrupo(grupoSel);
        } catch (e) { toast.error(mensajeError(e, 'Error al cambiar el estado')); }
    };

    const eliminarPermiso = async (p) => {
        if (!window.confirm(`¿Quitar el permiso ${p.permiso} sobre "${p.menu}"?`)) return;
        try {
            await axios.delete(getApiUrl(`/api/permisos/${p.id}`));
            cargarPermisosGrupo(grupoSel);
            cargarGrupos();
        } catch (e) { toast.error(mensajeError(e, 'Error al quitar el permiso')); }
    };

    // ===== Usuarios y su grupo =====

    const asignarGrupo = async (nombreUsuario, idGrupo) => {
        try {
            await axios.put(getApiUrl(`/api/usuarios/${encodeURIComponent(nombreUsuario)}/grupo`), { grupo: idGrupo || null });
            toast.success(idGrupo ? `${nombreUsuario} asignado a ${idGrupo}` : `${nombreUsuario} quedo sin grupo`);
            cargarUsuarios();
            cargarGrupos();
        } catch (e) { toast.error(mensajeError(e, 'Error al asignar el grupo')); }
    };

    const crearUsuario = async (e) => {
        e.preventDefault();
        const nombre = modalUsuario.usuario.trim();
        if (!nombre) { toast.warning('El nombre de usuario es obligatorio'); return; }
        if (modalUsuario.clave.length < 6) { toast.warning('La contrasena debe tener al menos 6 caracteres'); return; }
        if (modalUsuario.clave !== modalUsuario.clave2) { toast.warning('Las contrasenas no coinciden'); return; }
        try {
            await axios.post(getApiUrl('/api/usuarios'),
                { usuario: nombre, email: modalUsuario.email.trim() || null, clave: modalUsuario.clave, grupo: modalUsuario.grupo || null, idOperador: usuario || '' });
            toast.success(`Usuario ${nombre} creado`);
            setModalUsuario(null);
            await cargarUsuarios();
            cargarGrupos();
            setUsuarioSel(nombre);
        } catch (err) { toast.error(mensajeError(err, 'Error al crear el usuario')); }
    };

    const usuarioEliminado = () => {
        setUsuarioPendiente(false);
        setUsuarioSel(null);
        cargarUsuarios();
        cargarGrupos();
    };

    const usuariosDelGrupo = usuarios.filter(u => u.grupo === grupoSel);
    const usuariosFueraDelGrupo = usuarios.filter(u => u.grupo !== grupoSel);
    const usuarioDetalle = usuarios.find(u => u.usuario === usuarioSel) || null;
    const usuariosFiltrados = usuarios.filter(u => {
        const b = busquedaUsuario.toLowerCase();
        return !b || u.usuario.toLowerCase().includes(b) || (u.email || '').toLowerCase().includes(b) || (u.grupo || '').toLowerCase().includes(b);
    });

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Grupos y Permisos</h2>
                <div className="dperm-tabs">
                    <button className={vista === 'grupos' ? 'activo' : ''} onClick={() => cambiarVista('grupos')}>Grupos</button>
                    <button className={vista === 'usuarios' ? 'activo' : ''} onClick={() => cambiarVista('usuarios')}>Usuarios</button>
                </div>
            </div>

            {vista === 'grupos' && (
                <div className="dperm-layout">
                    {/* Lista de grupos */}
                    <div className="dperm-grupos">
                        <button className="btn btn-nuevo" style={{ width: '100%' }}
                                onClick={() => setModalGrupo({ edicion: false, idGrupo: '', nomGrupo: '' })}>+ Nuevo grupo</button>
                        {grupos.length === 0 && <p className="dperm-vacio">Todavia no hay grupos.</p>}
                        {grupos.map(g => (
                            <div key={g.idGrupo} className={`dperm-grupo ${g.idGrupo === grupoSel ? 'activo' : ''}`}
                                 onClick={() => seleccionarGrupo(g.idGrupo)}>
                                <strong>{g.idGrupo}</strong>
                                <span>{g.nomGrupo}</span>
                                <small>{g.usuarios} usuario(s) · {g.permisos} permiso(s)</small>
                            </div>
                        ))}
                    </div>

                    {/* Detalle del grupo */}
                    <div className="dperm-detalle">
                        {!grupo ? (
                            <p className="dperm-vacio">Seleccione un grupo para definir sus permisos.</p>
                        ) : (
                            <>
                                <div className="dperm-detalle-header">
                                    <h3>{grupo.idGrupo} <span>— {grupo.nomGrupo}</span></h3>
                                    <div>
                                        <button className="btn dperm-btn-sec"
                                                onClick={() => setModalGrupo({ edicion: true, idGrupo: grupo.idGrupo, nomGrupo: grupo.nomGrupo })}>Renombrar</button>
                                        <button className="btn btn-cancelar" onClick={eliminarGrupo}>Eliminar</button>
                                    </div>
                                </div>

                                <div className="dperm-tabs dperm-subtabs">
                                    <button className={subTab === 'accesos' ? 'activo' : ''} onClick={() => setSubTab('accesos')}>Accesos al menu ({marcadosGuardados.size})</button>
                                    <button className={subTab === 'especiales' ? 'activo' : ''} onClick={() => setSubTab('especiales')}>Permisos especiales ({especiales.length})</button>
                                    <button className={subTab === 'usuarios' ? 'activo' : ''} onClick={() => setSubTab('usuarios')}>Usuarios ({usuariosDelGrupo.length})</button>
                                </div>

                                {subTab === 'accesos' && (
                                    <div>
                                        <div className="dperm-barra">
                                            <input type="text" className="input-busqueda" placeholder="Buscar opcion..."
                                                   value={filtroMenu} onChange={(e) => setFiltroMenu(e.target.value)} />
                                            <button className="btn dperm-btn-sec" onClick={() => alternarAccesos([...idsHojas], true)}>Marcar todo</button>
                                            <button className="btn dperm-btn-sec" onClick={() => alternarAccesos([...idsHojas], false)}>Desmarcar todo</button>
                                            <span className="dperm-resumen">{marcados.size} de {idsHojas.size} opciones</span>
                                            <button className="btn btn-guardar" disabled={!hayCambios || guardando} onClick={guardarAccesos}>
                                                {guardando ? 'Guardando...' : hayCambios ? 'Guardar accesos' : 'Sin cambios'}
                                            </button>
                                        </div>
                                        <p className="dperm-nota">
                                            {marcadosGuardados.size === 0
                                                ? 'Este grupo no tiene accesos definidos: sus usuarios no ven ninguna opcion del menu (salvo las permitidas a cada uno individualmente).'
                                                : 'Los usuarios de este grupo solo ven en el menu las opciones marcadas (se aplica al volver a iniciar sesion).'}
                                        </p>
                                        <div className="dperm-arbol">
                                            {arbol.map(n => (
                                                <NodoMenu key={n.id} nodo={n} nivel={0} marcados={marcados} onToggle={alternarAccesos}
                                                          filtro={filtroMenu.trim().toLowerCase()} cerrados={cerrados} onPlegar={plegar} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {subTab === 'especiales' && (
                                    <div>
                                        <form className="dperm-barra" onSubmit={agregarEspecial}>
                                            <select className="form-input" value={nuevoEspecial.idMenu}
                                                    onChange={(e) => setNuevoEspecial({ ...nuevoEspecial, idMenu: e.target.value })}>
                                                <option value="">Opcion de menu...</option>
                                                {opcionesConPantalla.map(m => <option key={m.id} value={m.id}>{m.descripcion}</option>)}
                                            </select>
                                            <input className="form-input" list="dperm-catalogo" placeholder="Permiso (ej: Factura_timbrado_vencido)"
                                                   maxLength={30} value={nuevoEspecial.permiso}
                                                   onChange={(e) => setNuevoEspecial({ ...nuevoEspecial, permiso: e.target.value })} />
                                            <datalist id="dperm-catalogo">
                                                {catalogo.filter(p => p !== ACCESO).map(p => <option key={p} value={p} />)}
                                            </datalist>
                                            <button type="submit" className="btn btn-nuevo">+ Asignar</button>
                                        </form>
                                        <div className="tabla-container">
                                            <table className="tabla-datos">
                                                <thead><tr><th>Opcion de menu</th><th>Permiso</th><th>Estado</th><th>Acciones</th></tr></thead>
                                                <tbody>
                                                    {especiales.length === 0 ? (
                                                        <tr><td colSpan="4" className="sin-datos">El grupo no tiene permisos especiales</td></tr>
                                                    ) : especiales.map(p => (
                                                        <tr key={p.id}>
                                                            <td>{p.menu}</td>
                                                            <td><strong>{p.permiso}</strong></td>
                                                            <td>
                                                                <button className={`dperm-estado ${p.estado === 'A' ? 'activo' : ''}`}
                                                                        onClick={() => cambiarEstadoPermiso(p)} title="Cambiar estado">
                                                                    {p.estado === 'A' ? 'Activo' : 'Inactivo'}
                                                                </button>
                                                            </td>
                                                            <td><button className="btn-accion" title="Quitar" onClick={() => eliminarPermiso(p)}>🗑️</button></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {subTab === 'usuarios' && (
                                    <div>
                                        <div className="dperm-barra">
                                            <select className="form-input" value={usuarioAgregar} onChange={(e) => setUsuarioAgregar(e.target.value)}>
                                                <option value="">Agregar usuario al grupo...</option>
                                                {usuariosFueraDelGrupo.map(u => (
                                                    <option key={u.usuario} value={u.usuario}>{u.usuario}{u.grupo ? ` (hoy en ${u.grupo})` : ''}</option>
                                                ))}
                                            </select>
                                            <button className="btn btn-nuevo" disabled={!usuarioAgregar}
                                                    onClick={() => { asignarGrupo(usuarioAgregar, grupoSel); setUsuarioAgregar(''); }}>+ Agregar</button>
                                        </div>
                                        <div className="tabla-container">
                                            <table className="tabla-datos">
                                                <thead><tr><th>Usuario</th><th>Acciones</th></tr></thead>
                                                <tbody>
                                                    {usuariosDelGrupo.length === 0 ? (
                                                        <tr><td colSpan="2" className="sin-datos">El grupo no tiene usuarios</td></tr>
                                                    ) : usuariosDelGrupo.map(u => (
                                                        <tr key={u.usuario}>
                                                            <td><strong>{u.usuario}</strong></td>
                                                            <td><button className="btn dperm-btn-sec" onClick={() => asignarGrupo(u.usuario, null)}>Quitar del grupo</button></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {vista === 'usuarios' && (
                <div className="dperm-layout">
                    {/* Lista de usuarios */}
                    <div className="dperm-grupos">
                        <button className="btn btn-nuevo" style={{ width: '100%' }}
                                onClick={() => setModalUsuario({ usuario: '', email: '', clave: '', clave2: '', grupo: '' })}>+ Nuevo usuario</button>
                        <input type="text" className="input-busqueda" placeholder="Buscar usuario, correo o grupo..."
                               value={busquedaUsuario} onChange={(e) => setBusquedaUsuario(e.target.value)} />
                        <small className="dperm-resumen-lista">{usuarios.filter(u => !u.grupo).length} de {usuarios.length} usuarios sin grupo</small>
                        <div className="dperm-lista-usuarios">
                            {usuariosFiltrados.length === 0 && <p className="dperm-vacio">No hay usuarios para mostrar.</p>}
                            {usuariosFiltrados.map(u => (
                                <div key={u.usuario} className={`dperm-grupo ${u.usuario === usuarioSel ? 'activo' : ''}`}
                                     onClick={() => seleccionarUsuario(u.usuario)}>
                                    <strong>{u.usuario}</strong>
                                    {u.email && <small className="dperm-email">{u.email}</small>}
                                    <small>{u.grupo ? `${u.grupo} — ${u.nomGrupo || 'no catalogado'}` : 'sin grupo'}</small>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Permisos individuales del usuario */}
                    <div className="dperm-detalle">
                        {!usuarioDetalle ? (
                            <p className="dperm-vacio">Seleccione un usuario para asignarle grupo y definir sus permisos individuales.</p>
                        ) : (
                            <PermisosUsuario key={usuarioDetalle.usuario} usuarioSel={usuarioDetalle} grupos={grupos} menus={menus}
                                             arbol={arbol} idsHojas={idsHojas} catalogo={catalogo} operador={usuario}
                                             onAsignarGrupo={asignarGrupo} onCambio={() => { cargarGrupos(); cargarUsuarios(); }} onPendiente={setUsuarioPendiente}
                                             onEliminado={usuarioEliminado} />
                        )}
                    </div>
                </div>
            )}

            {modalUsuario && (
                <div className="modal-overlay">
                    <div className="modal-contenido" style={{ maxWidth: '460px' }}>
                        <div className="modal-header">
                            <h3>Nuevo usuario</h3>
                            <button onClick={() => setModalUsuario(null)} className="btn-cerrar">&times;</button>
                        </div>
                        <form onSubmit={crearUsuario} autoComplete="off">
                            <div className="form-group">
                                <label>Usuario * (max 32; letras, numeros y . _ @ -)</label>
                                <input type="text" className="form-input" value={modalUsuario.usuario} maxLength={32} autoFocus
                                       autoComplete="off" placeholder="Ej: jperez" onChange={(e) => setModalUsuario({ ...modalUsuario, usuario: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Correo electronico</label>
                                <input type="email" className="form-input" value={modalUsuario.email} maxLength={120}
                                       autoComplete="off" placeholder="Ej: jperez@empresa.com" onChange={(e) => setModalUsuario({ ...modalUsuario, email: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Contrasena * (minimo 6 caracteres)</label>
                                <input type="password" className="form-input" value={modalUsuario.clave} maxLength={72}
                                       autoComplete="new-password" onChange={(e) => setModalUsuario({ ...modalUsuario, clave: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Repetir contrasena *</label>
                                <input type="password" className="form-input" value={modalUsuario.clave2} maxLength={72}
                                       autoComplete="new-password" onChange={(e) => setModalUsuario({ ...modalUsuario, clave2: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Grupo</label>
                                <select className="form-input" value={modalUsuario.grupo} onChange={(e) => setModalUsuario({ ...modalUsuario, grupo: e.target.value })}>
                                    <option value="">(sin grupo: vera el menu completo)</option>
                                    {grupos.map(g => <option key={g.idGrupo} value={g.idGrupo}>{g.idGrupo} — {g.nomGrupo}</option>)}
                                </select>
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={() => setModalUsuario(null)} className="btn btn-cancelar">Cancelar</button>
                                <button type="submit" className="btn btn-guardar">Crear</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {modalGrupo && (
                <div className="modal-overlay">
                    <div className="modal-contenido" style={{ maxWidth: '460px' }}>
                        <div className="modal-header">
                            <h3>{modalGrupo.edicion ? 'Renombrar grupo' : 'Nuevo grupo'}</h3>
                            <button onClick={() => setModalGrupo(null)} className="btn-cerrar">&times;</button>
                        </div>
                        <form onSubmit={guardarGrupo}>
                            <div className="form-group">
                                <label>Codigo * (max 7 caracteres)</label>
                                <input type="text" className="form-input" value={modalGrupo.idGrupo} maxLength={7}
                                       disabled={modalGrupo.edicion} placeholder="Ej: ADMIN, VENTAS" autoFocus={!modalGrupo.edicion}
                                       onChange={(e) => setModalGrupo({ ...modalGrupo, idGrupo: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Nombre * (max 25 caracteres)</label>
                                <input type="text" className="form-input" value={modalGrupo.nomGrupo} maxLength={25}
                                       placeholder="Ej: Administradores" autoFocus={modalGrupo.edicion}
                                       onChange={(e) => setModalGrupo({ ...modalGrupo, nomGrupo: e.target.value })} />
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={() => setModalGrupo(null)} className="btn btn-cancelar">Cancelar</button>
                                <button type="submit" className="btn btn-guardar">{modalGrupo.edicion ? 'Actualizar' : 'Guardar'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DPermisos;
